import { api } from '@/lib/api';
import { fetchAuthorizedBlob } from '@shared/chat/authorized-media-fetch';
import type { ChatApi } from '@shared/chat/api-types';
import type { ChatRoom } from '@shared/chat/types';

export function createAdminChatApi(): ChatApi {
  return {
    getChatRooms: (includeArchived) => api.getChatRooms(includeArchived),
    getChatRoom: (roomId) => api.getChatRoom(roomId),
    getChatContacts: () => api.getChatContacts().then((r) => r.contacts ?? []),
    createChatRoom: (data) => api.createChatRoom(data as Parameters<typeof api.createChatRoom>[0]),
    updateRoom: (roomId, data) => api.updateRoom(roomId, data),
    deleteChatRoom: (roomId) => api.deleteChatRoom(roomId),
    muteRoom: (roomId, muted) => api.muteRoom(roomId, muted),
    archiveRoom: (roomId, archived) => api.archiveRoom(roomId, archived),
    markRoomRead: (roomId, lastMessageId, topicId) =>
      api.markRoomRead(roomId, lastMessageId, topicId),
    getRoomMessages: (roomId, before, limit, threadRootId, topicId) =>
      api.getRoomMessages(roomId, before, limit, threadRootId, topicId),
    sendMessage: (roomId, content, replyToId, linkPreview, scheduledAt, topicId) =>
      api.sendMessage(roomId, content, replyToId, linkPreview, scheduledAt, topicId),
    sendMessageWithAttachment: (roomId, file, options) =>
      api.sendMessageWithAttachment(roomId, file, options),
    uploadAttachment: (roomId, messageId, file, options) =>
      api.uploadAttachment(roomId, messageId, file, options),
    editMessage: (messageId, content) => api.editMessage(messageId, content),
    deleteMessage: (messageId) => api.deleteChatMessage(messageId),
    hideMessageForMe: (messageId) => api.hideMessageForMe(messageId),
    forwardMessage: (roomId, messageId, targetRoomId, targetTopicId) =>
      api.forwardChatMessage(roomId, messageId, targetRoomId, targetTopicId),
    searchMessages: (roomId, query, limit, includeThread) =>
      api.searchMessages(roomId, query, limit, includeThread),
    toggleReaction: (messageId, emoji) => api.toggleReaction(messageId, emoji),
    getLinkPreview: (url) => api.getLinkPreview(url),
    createPoll: (roomId, question, options, multiple) =>
      api.createPoll(roomId, question, options, multiple),
    votePoll: (pollId, optionIds) => api.votePoll(pollId, optionIds),
    pinMessage: (roomId, messageId) => api.pinMessage(roomId, messageId),
    getPinnedMessage: (roomId) => api.getPinnedMessage(roomId),
    getChatRoomTopics: (roomId, q) => api.getChatRoomTopics(roomId, q),
    createChatRoomTopic: (roomId, body) => api.createChatRoomTopic(roomId, body),
    patchChatRoomTopic: (roomId, topicId, body) => api.patchChatRoomTopic(roomId, topicId, body),
    deleteChatRoomTopic: (roomId, topicId) => api.deleteChatRoomTopic(roomId, topicId),
    setChatRoomForum: (roomId, enabled) => api.setChatRoomForum(roomId, enabled),
    getRoomMembers: (roomId) => api.getRoomMembers(roomId),
    addRoomMember: (roomId, body) => api.addRoomMember(roomId, body as { user_id: number; role?: string }),
    removeRoomMember: (roomId, memberId) => api.removeRoomMember(roomId, memberId),
    getRoomMedia: (roomId, params) =>
      api.getRoomMedia(roomId, {
        type: params?.type,
        limit: params?.limit,
        before: params?.before,
      }),
    uploadRoomAvatar: (roomId, file) => api.uploadRoomAvatar(roomId, file),
    uploadTopicIcon: (roomId, topicId, file) => api.uploadTopicIcon(roomId, topicId, file),
    buildAuthorizedFileUrl: (path) => api.buildAuthorizedFileUrl(path),
    buildChatAttachmentPreviewUrl: (fileUrl, thumbnailUrl) =>
      api.buildChatAttachmentPreviewUrl(fileUrl, thumbnailUrl),
    fetchAuthorizedBlob: (url, opts) =>
      fetchAuthorizedBlob(api.buildAuthorizedFileUrl(url), (input, init) => api.authedFetch(input, init), opts),
    issueWsTicket: () => api.issueWsTicket(),
    ensureSession: async (silent) => {
      if (typeof api.ensureSession === 'function') {
        return api.ensureSession(silent);
      }
      return '__cookie_session__';
    },
  };
}

export function developerInboxRoomTitle(room: ChatRoom): string {
  const org = (room.organization_name || '').trim();
  const author = (room.support_case_created_by_name || '').trim();
  if (room.entity_type === 'support_case') {
    const head = author || (room.name || '').trim() || 'Обращение в поддержку';
    return org ? `${head} — ${org}` : head;
  }
  const title = (room.name || '').trim() || `Чат #${room.id}`;
  return org ? `${org} — ${title}` : title;
}

export function isDeveloperGlobalSupportRoom(
  room: Pick<ChatRoom, 'entity_type' | 'name'>,
  supportGlobal?: boolean
): boolean {
  if (!supportGlobal) return false;
  if (room.entity_type === 'support' || room.entity_type === 'support_case') return true;
  return (room.name || '').toLowerCase().includes('поддержка');
}
